/**
 * Centralized HTTP client with consistent CSRF protection headers
 * Uses ky internally but provides a generic interface for HTTP requests
 */
import ky from "ky";

/**
 * Pre-configured HTTP client with CSRF protection headers and interceptors
 * Use this instead of raw HTTP libraries to ensure security headers are always included
 */
export const httpClient = ky.create({
  headers: {
    "X-Widget-Request": "true", // CSRF protection - required by server middleware
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  timeout: 30000, // 30 second timeout
  retry: {
    limit: 0, // Disable ky retries - let TanStack Query handle all retry logic
  },
  hooks: {
    beforeRequest: [
      (request) => {
        // Ensure CSRF header is always present (defensive)
        if (!request.headers.get("X-Widget-Request")) {
          request.headers.set("X-Widget-Request", "true");
        }

        // Add demo customer override header if present in localStorage
        const demoCustomerOverride = localStorage.getItem("demo-customer-override");
        if (demoCustomerOverride && !request.headers.get("X-Demo-Customer")) {
          request.headers.set("X-Demo-Customer", demoCustomerOverride);
        }
      },
    ],
  },
});

/**
 * Convenience methods for common HTTP operations
 * All methods automatically include CSRF protection headers and JSON handling
 *
 * Features included:
 * - Automatic CSRF header injection (defensive)
 * - JSON serialization/deserialization
 * - Built-in error handling via ky
 */
export const apiClient = {
  /**
   * GET request with automatic JSON parsing and security headers
   */
  get: <T = unknown>(url: string, options?: Parameters<typeof httpClient.get>[1]) =>
    httpClient.get(url, options).json<T>(),

  /**
   * POST request with automatic JSON handling and security headers
   */
  post: <T = unknown>(
    url: string,
    data?: unknown,
    options?: Parameters<typeof httpClient.post>[1],
  ) =>
    httpClient
      .post(url, {
        ...options,
        json: data,
      })
      .json<T>(),

  /**
   * PUT request with automatic JSON handling and security headers
   */
  put: <T = unknown>(url: string, data?: unknown, options?: Parameters<typeof httpClient.put>[1]) =>
    httpClient
      .put(url, {
        ...options,
        json: data,
      })
      .json<T>(),

  /**
   * PATCH request with automatic JSON handling and security headers
   */
  patch: <T = unknown>(
    url: string,
    data?: unknown,
    options?: Parameters<typeof httpClient.patch>[1],
  ) =>
    httpClient
      .patch(url, {
        ...options,
        json: data,
      })
      .json<T>(),

  /**
   * DELETE request with automatic JSON parsing and security headers
   */
  delete: <T = unknown>(url: string, options?: Parameters<typeof httpClient.delete>[1]) =>
    httpClient.delete(url, options).json<T>(),
} as const;
